(() => {
  if (customElements.get('nm-email-promotion')) return;
  const storageKey = 'nimavo_email_promotion_source';
  const upper = value => String(value || '').trim().toUpperCase();
  class EmailPromotion extends HTMLElement {
    connectedCallback() {
      const header = document.querySelector('nm-cart .nm-cart-header');
      if (header && this.parentElement !== header.parentElement) {
        header.after(this);
        return;
      }
      if (this.started) return;
      this.config = JSON.parse(this.querySelector('[data-email-config]').textContent);
      this.calendar = this.config.calendar.map(day => ({
        ...day, codes: day.codes.map(upper), start: Date.parse(day.date + 'T00:00:00-05:00'),
        end: Date.parse(day.date + 'T00:00:00-05:00') + 86400000
      })).filter(day => Number.isFinite(day.start) && day.codes.every(Boolean));
      this.codes = new Set(this.calendar.flatMap(day => day.codes));
      const url = new URL(location.href);
      const incoming = (url.searchParams.get('discount') || '').split(',').map(upper).find(code => this.codes.has(code));
      let remembered;
      try { remembered = localStorage.getItem(storageKey); } catch (_) { /* Storage may be disabled. */ }
      const source = incoming || upper(url.searchParams.get('nm_promo'));
      if (!this.codes.has(source)) {
        if (this.codes.has(upper(remembered)) || document.querySelector('nm-cart[data-nm-email-discount="true"]')) {
          this.started = true;
          this.root = window.Shopify?.routes?.root || '/';
          this.controller = new AbortController();
          this.restoreRegularPrices();
        }
        return;
      }
      this.started = true;
      this.source = source;
      try { localStorage.setItem(storageKey, source); } catch (_) { /* The URL also retains eligibility. */ }
      url.searchParams.set('nm_promo', source);
      history.replaceState(history.state, '', url);
      this.root = window.Shopify?.routes?.root || '/';
      this.controller = new AbortController();
      this.label = this.querySelector('[data-email-label]');
      this.detail = this.querySelector('[data-email-detail]');
      this.clock = this.querySelector('[data-email-clock]');
      this.retry = this.querySelector('[data-email-retry]');
      this.hidden = false;
      const legacyTimer = document.querySelector('[data-nm-cart-timer]');
      if (legacyTimer) legacyTimer.hidden = true;
      const options = {signal: this.controller.signal};
      this.retry.addEventListener('click', () => this.sync(), options);
      document.addEventListener('cart:updated', () => this.sync(), options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.sync();
      }, options);
      window.addEventListener('pageshow', event => { if (event.persisted) this.sync(); }, options);
      document.addEventListener('click', event => {
        const checkout = event.target.closest?.('#rsi_buy_now_button,.rsi-submit_button,[name="checkout"]');
        if (!checkout) return;
        if (!this.ready || this.busy || this.dayKey() !== this.confirmedDay || this.cartBusy()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeCod();
          this.sync();
        }
      }, {...options, capture:true});
      this.sync();
    }

    async restoreRegularPrices() {
      if (this.busy) return;
      this.busy = true;
      this.failed = false;
      if (!this.regularGuard) {
        this.regularGuard = true;
        document.addEventListener('click', event => {
          if (!this.busy && !this.failed) return;
          if (!event.target.closest?.('#rsi_buy_now_button,.rsi-submit_button,[name="checkout"]')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, {signal: this.controller.signal, capture: true});
        this.querySelector('[data-email-retry]').addEventListener('click', () => this.restoreRegularPrices(), {signal: this.controller.signal});
      }
      this.closeCod();
      try {
        const cart = await this.request('cart.js');
        const codes = this.appliedCodes(cart);
        const emailCodes = codes.filter(code => this.codes.has(code));
        if (emailCodes.length) {
          const keep = codes.filter(code => !this.codes.has(code));
          const updated = await this.request('cart/update.js', {discount: keep.join(',')});
          if (this.appliedCodes(updated).some(code => this.codes.has(code))) throw new Error('Regular prices unavailable');
          try { localStorage.removeItem(storageKey); } catch (_) {}
          const url = new URL(location.href);
          url.searchParams.delete('nm_promo');
          url.searchParams.delete('discount');
          if (keep.length) url.searchParams.set('discount', keep.join(','));
          location.replace(url.href);
          return;
        }
        try { localStorage.removeItem(storageKey); } catch (_) {}
        this.busy = false;
        this.hidden = true;
      } catch (_) {
        this.busy = false;
        this.failed = true;
        this.hidden = false;
        this.querySelector('[data-email-label]').textContent = this.config.errorLabel;
        this.querySelector('[data-email-clock]').hidden = true;
        this.querySelector('[data-email-detail]').hidden = true;
        this.querySelector('[data-email-retry]').hidden = false;
        this.querySelector('[data-email-status]').textContent = this.config.errorLabel;
      }
    }

    now() { return this.serverTime === undefined ? Date.now() : this.serverTime + performance.now() - this.serverAnchor; }
    activeDay() { const now = this.now(); return this.calendar.find(day => now >= day.start && now < day.end); }
    dayKey() { return this.activeDay()?.date || ''; }
    cartBusy() { const cart = document.querySelector('nm-cart'); return cart?.busy || cart?.pending?.size || cart?.refreshing; }
    closeCod() { document.querySelector('#rsi_form_wrapper[open] .rsi-form-close-button')?.click(); }

    draw() {
      clearTimeout(this.tick);
      if (!this.isConnected) return;
      const day = this.activeDay();
      if (this.ready && this.dayKey() !== this.confirmedDay) {
        this.ready = false;
        this.closeCod();
        this.sync();
      }
      this.clock.hidden = !day || !this.ready;
      this.detail.hidden = !day || !this.ready;
      this.label.textContent = this.failed ? this.config.errorLabel : !this.ready ? this.config.busyLabel : day ? this.config.activeLabel : this.config.inactiveLabel;
      this.retry.hidden = !this.failed;
      if (day && this.ready) {
        const seconds = Math.max(0, Math.ceil((day.end - this.now()) / 1000));
        this.clock.textContent = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map(n => String(n).padStart(2, '0')).join(':');
      }
      const next = day ? Math.min(1000, Math.max(1, day.end - this.now())) : Math.min(...this.calendar.filter(d => d.start > this.now()).map(d => d.start - this.now()), 60000);
      this.tick = setTimeout(() => this.draw(), Math.max(1, next));
    }

    appliedCodes(cart) {
      const codes = (cart.discount_codes || []).map(d => typeof d === 'string' ? d : d.code);
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))];
      applications.forEach(app => { if (app?.type === 'discount_code') codes.push(app.code || app.title); });
      return [...new Set(codes.map(upper).filter(Boolean))];
    }

    async request(path, body) {
      const abort = new AbortController();
      this.requestAbort = abort;
      const timeout = setTimeout(() => abort.abort(), 15000);
      try {
        const response = await fetch(this.root + path, {
          method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
          headers:{Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {})},
          ...(body ? {body:JSON.stringify(body)} : {}), signal:abort.signal
        });
        if (!response.ok) throw new Error('Promotion cart unavailable');
        const server = Date.parse(response.headers.get('Date'));
        if (Number.isFinite(server)) { this.serverTime = server; this.serverAnchor = performance.now(); }
        return await response.json();
      } finally { clearTimeout(timeout); if (this.requestAbort === abort) this.requestAbort = null; }
    }

    async sync() {
      if (!this.isConnected) return;
      if (this.busy) { this.syncQueued = true; return; }
      if (this.cartBusy()) {
        clearTimeout(this.deferSync);
        this.deferSync = setTimeout(() => this.sync(), 150);
        return;
      }
      this.busy = true;
      this.ready = false;
      this.failed = false;
      this.draw();
      try {
        const cart = await this.request('cart.js');
        if (this.cartBusy()) { this.deferSync = setTimeout(() => this.sync(), 150); return; }
        const day = this.activeDay();
        const blanketItems = cart.items.filter(item => Number(item.product_id) === this.config.productId);
        const quantity = blanketItems.reduce((sum, item) => sum + item.quantity, 0);
        const tier = quantity >= 1 && quantity <= 3 ? quantity - 1 : -1;
        const desired = day && tier >= 0 ? day.codes[tier] : '';
        const codes = this.appliedCodes(cart);
        const emailCodes = codes.filter(code => this.codes.has(code));
        const url = new URL(location.href);
        const queryCodes = (url.searchParams.get('discount') || '').split(',').map(upper).filter(Boolean);
        const previousEmail = queryCodes.filter(code => this.codes.has(code));
        const blanketTotal = blanketItems.reduce((sum, item) => sum + item.final_line_price, 0);
        const correctPrice = !!desired && blanketTotal === day.prices[tier];
        const wrongCode = emailCodes.some(code => code !== desired);
        const needsUpdate = desired ? !correctPrice || wrongCode : emailCodes.length > 0 || previousEmail.length > 0;
        if (needsUpdate) {
          this.closeCod();
          const keep = [...new Set([...codes, ...queryCodes].filter(code => !this.codes.has(code)))];
          if (desired) keep.push(desired);
          const updated = await this.request('cart/update.js', {discount:keep.join(',')});
          if (desired) {
            const total = updated.items.filter(item => Number(item.product_id) === this.config.productId).reduce((sum, item) => sum + item.final_line_price, 0);
            if (total !== day.prices[tier]) throw new Error('Promotion price unavailable');
          }
          if (keep.length) url.searchParams.set('discount', keep.join(','));
          else url.searchParams.delete('discount');
          url.searchParams.set('nm_promo', this.source);
          location.replace(url.href);
          return;
        }
        if (desired && !queryCodes.includes(desired)) {
          url.searchParams.set('discount', [...queryCodes.filter(code => !this.codes.has(code)), desired].join(','));
          location.replace(url.href);
          return;
        }
        this.confirmedDay = day?.date || '';
        this.ready = true;
      } catch (_) {
        this.failed = true;
        this.ready = false;
        this.closeCod();
        this.querySelector('[data-email-status]').textContent = this.config.errorLabel;
      } finally {
        this.busy = false;
        this.draw();
        if (this.syncQueued && this.isConnected) {
          this.syncQueued = false;
          this.deferSync = setTimeout(() => this.sync(), 150);
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestAbort?.abort();
      clearTimeout(this.tick);
      clearTimeout(this.deferSync);
      this.started = false;
    }
  }
  customElements.define('nm-email-promotion', EmailPromotion);
})();
